/**
 * 1.3.96: 特定商業取引法に基づく表記ページ
 * 日本の法律に基づく商業情報開示ページ
 * 個人事業主向け：プライバシー保護のため情報省略形式を使用
 * 1.4.3: 特定商取引法テンプレートに基づく項目順序の再編成
 */

import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export const CommercialDisclosurePage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="戻る"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <h1 className="text-xl font-bold text-gray-900">特定商取引法に基づく表記</h1>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Title Section */}
          <div className="bg-gradient-to-r from-primary to-purple-600 p-8 text-white">
            <h2 className="text-2xl font-bold mb-2">特定商取引法に基づく表記</h2>
            <p className="text-purple-100 text-sm">
              Disclosure Based on the Specified Commercial Transactions Act
            </p>
          </div>

          {/* Disclosure Content */}
          <div className="p-8">
            <table className="w-full">
              <tbody className="divide-y divide-gray-100">
                {/* 1. 法人名（販売事業者名） */}
                <tr className="disclosure-row">
                  <th className="disclosure-label">販売事業者名</th>
                  <td className="disclosure-value">
                    付 昂
                  </td>
                </tr>

                {/* 2. 住所（所在地） */}
                <tr className="disclosure-row">
                  <th className="disclosure-label">所在地</th>
                  <td className="disclosure-value">
                    〒342-0038 埼玉県吉川市美南2丁目25-1-301
                  </td>
                </tr>

                {/* 3. 電話番号 */}
                <tr className="disclosure-row">
                  <th className="disclosure-label">電話番号</th>
                  <td className="disclosure-value">
                    080-4441-3968
                    <br />
                    <span className="text-sm text-gray-500">
                      営業時間 10:00〜18:00（土日祝日を除く）
                    </span>
                  </td>
                </tr>

                {/* 4. メールアドレス */}
                <tr className="disclosure-row">
                  <th className="disclosure-label">メールアドレス</th>
                  <td className="disclosure-value">
                    <a 
                      href="mailto:contact@yuichat.app" 
                      className="text-primary hover:text-primary-dark transition-colors"
                    >
                      contact@yuichat.app
                    </a>
                  </td>
                </tr>

                {/* 5. 運営責任者 */}
                <tr className="disclosure-row">
                  <th className="disclosure-label">運営責任者</th>
                  <td className="disclosure-value">
                    付 昂
                  </td>
                </tr>

                {/* 6. 追加手数料（商品代金以外の必要料金） */}
                <tr className="disclosure-row">
                  <th className="disclosure-label">商品代金以外の必要料金</th>
                  <td className="disclosure-value">
                    なし
                    <br />
                    <span className="text-sm text-gray-500">
                      ※インターネット接続料金はお客様のご負担となります
                    </span>
                  </td>
                </tr>

                {/* 7. 交換および返品に関するポリシー */}
                <tr className="disclosure-row">
                  <th className="disclosure-label">返品・キャンセルについて</th>
                  <td className="disclosure-value">
                    <div className="space-y-3">
                      <div>
                        <p className="font-medium text-gray-800 mb-1">＜サブスクリプションのキャンセル＞</p>
                        <p>
                          サブスクリプションのキャンセルはいつでも可能です。
                          キャンセル後も、現在の請求期間の終了まではサービスをご利用いただけます。
                        </p>
                      </div>
                      <div>
                        <p className="font-medium text-gray-800 mb-1">＜返金について＞</p>
                        <p>
                          デジタルサービスの性質上、サービス提供開始後の返金には原則として応じかねます。
                          ただし、サービスに重大な不具合があった場合は、カスタマーサポート
                          （<a href="mailto:contact@yuichat.app" className="text-primary hover:text-primary-dark">contact@yuichat.app</a>）
                          にお問い合わせください。
                        </p>
                      </div>
                      <p className="text-sm text-gray-500">
                        ※無料トライアル期間中のキャンセルについては、課金は発生いたしません
                      </p>
                    </div>
                  </td>
                </tr>

                {/* 8. 配達時間（サービス提供時期） */}
                <tr className="disclosure-row">
                  <th className="disclosure-label">サービス提供時期</th>
                  <td className="disclosure-value">
                    決済完了後、即時ご利用可能
                  </td>
                </tr>

                {/* 9. 利用可能な決済手段 */}
                <tr className="disclosure-row">
                  <th className="disclosure-label">利用可能な決済手段</th>
                  <td className="disclosure-value">
                    クレジットカード決済（Stripe）
                    <br />
                    <span className="text-sm text-gray-500">
                      VISA / Mastercard / American Express / JCB
                    </span>
                  </td>
                </tr>

                {/* 10. 決済期間（支払時期） */}
                <tr className="disclosure-row">
                  <th className="disclosure-label">支払時期</th>
                  <td className="disclosure-value">
                    クレジットカード決済はただちに処理されます
                    <br />
                    <span className="text-sm text-gray-500">
                      サブスクリプションは毎月自動更新（更新日に自動決済）
                    </span>
                  </td>
                </tr>

                {/* 11. 価格（販売価格） */}
                <tr className="disclosure-row">
                  <th className="disclosure-label">販売価格</th>
                  <td className="disclosure-value">
                    <a 
                      href="/#pricing"
                      onClick={(e) => {
                        e.preventDefault();
                        sessionStorage.setItem('scrollToPricing', 'true');
                        window.location.href = '/';
                      }}
                      className="text-primary hover:text-primary-dark transition-colors cursor-pointer"
                    >
                      各プラン・サービスページに表示された価格
                    </a>
                    に準じます
                    <br />
                    <span className="text-sm text-gray-500">
                      ※価格は全て税込表示です
                    </span>
                  </td>
                </tr>

                {/* オプション項目：動作環境 */}
                <tr className="disclosure-row">
                  <th className="disclosure-label">動作環境</th>
                  <td className="disclosure-value">
                    <div className="space-y-1">
                      <p>【推奨ブラウザ】</p>
                      <ul className="list-disc list-inside text-sm text-gray-600 ml-2">
                        <li>Google Chrome（最新版）</li>
                        <li>Mozilla Firefox（最新版）</li>
                        <li>Microsoft Edge（最新版）</li>
                        <li>Safari（最新版）</li>
                      </ul>
                      <p className="text-sm text-gray-500 mt-2">
                        ※JavaScriptを有効にする必要があります
                      </p>
                    </div>
                  </td>
                </tr>

                {/* 販売URL */}
                <tr className="disclosure-row">
                  <th className="disclosure-label">販売URL</th>
                  <td className="disclosure-value">
                    <a 
                      href="https://yuichat.app" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:text-primary-dark transition-colors"
                    >
                      https://yuichat.app
                    </a>
                  </td>
                </tr>

                {/* 特別条件 */}
                <tr className="disclosure-row">
                  <th className="disclosure-label">特別条件</th>
                  <td className="disclosure-value">
                    <div className="space-y-2">
                      <p>
                        ご利用にあたっては、
                        <a 
                          href="/terms" 
                          className="text-primary hover:text-primary-dark transition-colors mx-1"
                        >
                          利用規約
                        </a>
                        への同意が必要です。
                      </p>
                      <p className="text-sm text-gray-500">
                        ※サービス内容は予告なく変更される場合があります
                      </p>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Note Section */}
            <div className="mt-8 p-6 bg-purple-50 rounded-xl border border-purple-100">
              <h3 className="text-sm font-semibold text-purple-900 mb-3">
                【お問い合わせについて】
              </h3>
              <p className="text-sm text-purple-800 leading-relaxed">
                サービスに関するご質問・ご相談は、上記メールアドレスまたは電話番号までお気軽にお問い合わせください。
              </p>
            </div>

            {/* Last Updated */}
            <div className="mt-8 text-center text-sm text-gray-400">
              最終更新日：2026年2月2日
            </div>
          </div>
        </div>

        {/* Back Button */}
        <div className="mt-8 text-center">
          <button
            onClick={() => {
              // 1.3.84: 使用 sessionStorage 传递滚动意图，更可靠
              sessionStorage.setItem('scrollToFooter', 'true');
              window.location.href = '/';
            }}
            className="px-6 py-3 text-primary hover:text-primary-dark font-medium transition-colors"
          >
            ← トップページに戻る
          </button>
        </div>
      </main>

      {/* Styles */}
      <style>{`
        .disclosure-row {
          display: flex;
          flex-direction: column;
          padding: 1.25rem 0;
        }
        
        @media (min-width: 640px) {
          .disclosure-row {
            display: table-row;
          }
        }
        
        .disclosure-label {
          font-weight: 600;
          color: #374151;
          padding: 0.75rem 1rem 0.5rem 0;
          text-align: left;
          vertical-align: top;
          white-space: nowrap;
          width: 180px;
          font-size: 0.9rem;
        }
        
        @media (min-width: 640px) {
          .disclosure-label {
            padding: 1rem 1.5rem 1rem 0;
          }
        }
        
        .disclosure-value {
          color: #1f2937;
          padding: 0.5rem 0 0.75rem 0;
          line-height: 1.7;
        }
        
        @media (min-width: 640px) {
          .disclosure-value {
            padding: 1rem 0;
          }
        }
      `}</style>
    </div>
  );
};
